/**
 * Uber guest-trip status catalogue.
 *
 * Every status string the portal understands lives here, together with how it
 * should be labelled, coloured, and which tab it belongs to. When the real Uber
 * API contract lands, this is the ONLY file that needs the status strings
 * corrected — the components read groups and descriptors, never raw strings.
 *
 * Backend contract (matches the schemas already in the corporate portal):
 *
 *   LISTING  <- Trip collection (existing user DB)
 *     trip.uberBooked             Boolean
 *     trip.uberDetails.uberStatus String   <- the values below
 *     trip.uberDetails.driver     Object
 *     trip.uberDetails.vehicle    Object
 *
 *   DETAIL   <- UberTrip collection (already in the DB, keyed by tripId)
 *     history[]  { status, timestamp, message, rawEvent }
 *     trackingUrl, eta / pickupEta / dropEta
 *     fare { estimated, final, currencyCode }, receiptUrl, receiptStatus
 *     reBooking, oldUberTripId
 */

export const UBER_STATUS = {
  // pre-dispatch
  PENDING: 'pending',
  SCHEDULED: 'scheduled',
  RESERVE: 'reserve',
  PROCESSING: 'processing',

  // a driver is assigned, but the car is not on its way yet
  ACCEPTED: 'accepted',
  DRIVER_REDISPATCHED: 'driver_redispatched',
  // Uber names a driver ahead of time for a Reserve, well before pickup.
  UPFRONT_DRIVER_ASSIGNED: 'upfront_driver_assigned',

  // dispatched / live
  ARRIVING: 'arriving',
  IN_PROGRESS: 'in_progress',

  // terminal - good
  COMPLETED: 'completed',

  // terminal - needs attention
  NO_DRIVERS_AVAILABLE: 'no_drivers_available',
  DRIVER_CANCELED: 'driver_canceled',
  // Not a webhook status - there is no booking for Uber to report on. It is
  // stamped on the trip when a create attempt is refused, with
  // uberDetails.failureReason and .failedAt alongside.
  BOOKING_FAILED: 'booking_failed',

  // terminal - expected cancellations
  CANCELLATION_PENDING: 'cancellation_pending',
  RIDER_CANCELED: 'rider_canceled',
  CANCELED: 'canceled',
  // Who pressed cancel. Uber reports both as rider_canceled - it cannot tell an
  // operator in the portal from the crew on their ride link - so the server
  // records which and lands the confirming webhook on one of these.
  ADMIN_CANCELED: 'admin_canceled',
  USER_CANCELED: 'user_canceled',
  // Cancelled because the trip was rescheduled by another service rather than by
  // anyone here. It reads as a reschedule, not as someone changing their mind.
  RESCHEDULE_CANCELED: 'reschedule_canceled',
  EXPIRED: 'expired',
};

export const UBER_TRIP_TYPE = {
  ON_DEMAND: 'on_demand',
  SCHEDULED: 'scheduled',
  RESERVE: 'reserve',
};

export const TRIP_TYPE_OPTIONS = [
  {
    id: UBER_TRIP_TYPE.ON_DEMAND,
    name: 'On Demand',
    hint: 'Dispatch a driver right now.',
    icon: 'fas fa-bolt',
    requiresTime: false,
  },
  {
    id: UBER_TRIP_TYPE.SCHEDULED,
    name: 'Scheduled',
    hint: 'Uber dispatches close to the pickup time.',
    icon: 'far fa-clock',
    requiresTime: true,
  },
  {
    id: UBER_TRIP_TYPE.RESERVE,
    name: 'Reserve',
    hint: 'Guaranteed pickup, booked well in advance.',
    icon: 'fas fa-calendar-check',
    requiresTime: true,
  },
];

/**
 * The two states that mean "this trip is not going to happen unless someone
 * does something". These drive the Attention tab, the badge count and the
 * realtime alert banner.
 */
export const CRITICAL_STATUSES = [
  UBER_STATUS.NO_DRIVERS_AVAILABLE,
  UBER_STATUS.DRIVER_CANCELED,
];

export const STATUS_GROUP = {
  ONGOING: 'ongoing',
  SCHEDULED: 'scheduled',
  ATTENTION: 'attention',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

const DESCRIPTORS = {
  // Booked, car not yet on its way to the crew - these all sit on the Scheduled
  // tab, which mirrors status.js on the server.
  [UBER_STATUS.PENDING]: {
    label: 'Booking',
    color: '#d59e1d',
    group: STATUS_GROUP.SCHEDULED,
  },
  [UBER_STATUS.PROCESSING]: {
    label: 'Finding Driver',
    color: '#d59e1d',
    group: STATUS_GROUP.SCHEDULED,
  },
  [UBER_STATUS.SCHEDULED]: {
    label: 'Scheduled',
    color: '#5b6bbf',
    group: STATUS_GROUP.SCHEDULED,
  },
  [UBER_STATUS.RESERVE]: {
    label: 'Reserved',
    color: '#3f51b5',
    group: STATUS_GROUP.SCHEDULED,
  },
  // A driver is assigned, which for an advance booking happens long before
  // pickup - so these stay on Scheduled. Ongoing starts at `arriving`.
  [UBER_STATUS.ACCEPTED]: {
    label: 'Driver Assigned',
    color: '#2e9e5b',
    group: STATUS_GROUP.SCHEDULED,
  },
  [UBER_STATUS.DRIVER_REDISPATCHED]: {
    label: 'Driver Reassigned',
    color: '#2e9e5b',
    group: STATUS_GROUP.SCHEDULED,
  },
  // Uber can name a Reserve driver before that driver has accepted. This is
  // still a reserved ride from the portal user's perspective; the underlying
  // status stays distinct so a later `accepted` webhook can advance it.
  [UBER_STATUS.UPFRONT_DRIVER_ASSIGNED]: {
    label: 'Reserved',
    color: '#3f51b5',
    group: STATUS_GROUP.SCHEDULED,
  },
  [UBER_STATUS.ARRIVING]: {
    label: 'Arriving',
    color: '#2e9e5b',
    group: STATUS_GROUP.ONGOING,
  },
  [UBER_STATUS.IN_PROGRESS]: {
    label: 'Started',
    color: '#1b7f43',
    group: STATUS_GROUP.ONGOING,
  },
  [UBER_STATUS.COMPLETED]: {
    label: 'Ended',
    color: '#4a4a4a',
    group: STATUS_GROUP.COMPLETED,
  },
  [UBER_STATUS.NO_DRIVERS_AVAILABLE]: {
    label: 'No Driver Available',
    color: '#c62828',
    group: STATUS_GROUP.ATTENTION,
    critical: true,
  },
  [UBER_STATUS.DRIVER_CANCELED]: {
    label: 'Driver Cancelled',
    color: '#c62828',
    group: STATUS_GROUP.ATTENTION,
    critical: true,
  },
  // A deeper red than the other two on purpose: those mean a booking existed
  // and then died, this one means no booking was ever made.
  [UBER_STATUS.BOOKING_FAILED]: {
    label: 'Booking Failed',
    color: '#a01818',
    group: STATUS_GROUP.ATTENTION,
    critical: true,
  },
  // Set by our own cancel, before Uber's confirming webhook lands.
  [UBER_STATUS.CANCELLATION_PENDING]: {
    label: 'Cancelling',
    color: '#a1887f',
    group: STATUS_GROUP.CANCELLED,
  },
  [UBER_STATUS.RIDER_CANCELED]: {
    label: 'Rider Cancelled',
    color: '#8a8a8a',
    group: STATUS_GROUP.CANCELLED,
  },
  [UBER_STATUS.CANCELED]: {
    label: 'Cancelled',
    color: '#8a8a8a',
    group: STATUS_GROUP.CANCELLED,
  },
  [UBER_STATUS.ADMIN_CANCELED]: {
    label: 'Admin Cancelled',
    color: '#8a8a8a',
    group: STATUS_GROUP.CANCELLED,
  },
  [UBER_STATUS.USER_CANCELED]: {
    label: 'User Cancelled',
    color: '#8a8a8a',
    group: STATUS_GROUP.CANCELLED,
  },
  [UBER_STATUS.RESCHEDULE_CANCELED]: {
    label: 'Cancelled - Rescheduled',
    color: '#8a8a8a',
    group: STATUS_GROUP.CANCELLED,
  },
  [UBER_STATUS.EXPIRED]: {
    label: 'Expired',
    color: '#8a8a8a',
    group: STATUS_GROUP.CANCELLED,
  },
};

const UNKNOWN = { label: 'Unknown', color: '#8a8a8a', group: STATUS_GROUP.ONGOING };

/**
 * Turns a raw status key into a readable label: underscores become spaces and
 * every word is capitalised.
 *
 *   rebooked            -> Rebooked
 *   receipt_ready       -> Receipt Ready
 *   pickup_time_updated -> Pickup Time Updated
 *
 * Used for the timeline-only events that have no descriptor of their own. Those
 * are not tab statuses, so giving them entries in DESCRIPTORS would leak them
 * into the tabs' status filters via statusesForGroup().
 */
function humanise(status) {
  return String(status)
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function statusDescriptor(status) {
  if (!status) return UNKNOWN;
  return DESCRIPTORS[String(status).toLowerCase()] || { ...UNKNOWN, label: humanise(status) };
}

export function statusLabel(status) {
  return statusDescriptor(status).label;
}

export function statusColor(status) {
  return statusDescriptor(status).color;
}

export function isCritical(status) {
  return !!statusDescriptor(status).critical;
}

/**
 * Uber may supply a provisional driver's details for a Reserve before the
 * driver accepts it. Do not expose those details until the accepted event.
 */
export function shouldShowDriverDetails(status) {
  return String(status || '').toLowerCase() !== UBER_STATUS.UPFRONT_DRIVER_ASSIGNED;
}

/**
 * Statuses belonging to a tab group — sent to the backend as a filter so the
 * server does the narrowing rather than the client over-fetching.
 */
export function statusesForGroup(group) {
  return Object.keys(DESCRIPTORS).filter((key) => DESCRIPTORS[key].group === group);
}

/**
 * Whether a trip can still be cancelled from the portal.
 *
 * Everything holding a live booking except `in_progress` - Uber refuses to
 * cancel a ride with the crew already on board. Mirrors CANCELLABLE_STATUSES in
 * status.js on the server, which is the side that enforces it.
 */
export function isCancellable(status) {
  return [
    UBER_STATUS.PENDING,
    UBER_STATUS.SCHEDULED,
    UBER_STATUS.RESERVE,
    UBER_STATUS.PROCESSING,
    UBER_STATUS.ACCEPTED,
    UBER_STATUS.DRIVER_REDISPATCHED,
    UBER_STATUS.UPFRONT_DRIVER_ASSIGNED,
    UBER_STATUS.ARRIVING,
  ].includes(String(status || '').toLowerCase());
}

/**
 * Support actions, gated by status.
 *
 * These decide what renders; the server enforces the same lists (CALLABLE_STATUSES
 * / MESSAGEABLE_STATUSES / COMPLETED_STATUSES in status.js) and is what actually
 * refuses the action.
 */

/** A driver must exist to be rung. Completed included, for a bag left in the car. */
export function canCallDriver(status) {
  return [
    UBER_STATUS.ACCEPTED,
    UBER_STATUS.DRIVER_REDISPATCHED,
    UBER_STATUS.ARRIVING,
    UBER_STATUS.IN_PROGRESS,
    UBER_STATUS.COMPLETED,
  ].includes(String(status || '').toLowerCase());
}

/**
 * Starts at `accepted` like a call - while Uber is still finding a driver there
 * is nobody to message. Stops before `completed`, unlike a call: Uber's message
 * endpoint needs a live ride.
 */
export function canMessageDriver(status) {
  return [
    UBER_STATUS.ACCEPTED,
    UBER_STATUS.DRIVER_REDISPATCHED,
    UBER_STATUS.ARRIVING,
    UBER_STATUS.IN_PROGRESS,
  ].includes(String(status || '').toLowerCase());
}

/**
 * Whether a driver has been assigned and the ride is still to happen.
 *
 * Not the same as "a driver object exists" - those details can lag behind the
 * status by a webhook. Ends at `completed`, where the ride PIN and anything else
 * about meeting the driver stops meaning anything.
 */
export function hasDriverAssigned(status) {
  return [
    UBER_STATUS.ACCEPTED,
    UBER_STATUS.DRIVER_REDISPATCHED,
    UBER_STATUS.ARRIVING,
    UBER_STATUS.IN_PROGRESS,
  ].includes(String(status || '').toLowerCase());
}

/** Complaints and safety escalations are raised after the ride. */
export function canReportIssue(status) {
  return String(status || '').toLowerCase() === UBER_STATUS.COMPLETED;
}

/**
 * What can be reported about a finished trip.
 *
 * Mirrors the enum on TripIssue, minus 'safety' which has its own flow. Shared
 * rather than declared per component: the portal drawer and the crew's ride link
 * both offer this list, and two hand-maintained copies would drift the first
 * time the enum changes.
 */
export const ISSUE_TYPES = [
  { value: 'driver_behavior', label: 'Driver behaviour' },
  { value: 'route_issue', label: 'Route taken' },
  { value: 'billing', label: 'Billing / fare' },
  { value: 'other', label: 'Something else' },
];

/**
 * How far a trip's receipt has got. Mirrors RECEIPT_STATUS in status.js on the
 * server, which is the side that writes it.
 */
export const RECEIPT_STATUS = {
  NONE: 0,
  READY: 1,
  RETRIEVED: 2,
};

/**
 * Whether there is a receipt to offer.
 *
 * Gated on the flag, not merely on the trip being over: Uber works the fare out
 * after the ride ends and tells us when the receipt exists. Offering a button
 * the moment a trip completes would mean offering one that fails.
 */
export function hasReceipt(status, receiptStatus) {
  const value = String(status || '').toLowerCase();
  const settled =
    value === UBER_STATUS.COMPLETED ||
    // A cancellation can still be charged for, and a cancellation fee is
    // exactly the charge somebody wants evidence of. Mirrors
    // RECEIPTABLE_STATUSES on the server, which is the side that enforces it.
    value === UBER_STATUS.RIDER_CANCELED ||
    value === UBER_STATUS.CANCELED ||
    value === UBER_STATUS.ADMIN_CANCELED ||
    value === UBER_STATUS.USER_CANCELED ||
    value === UBER_STATUS.DRIVER_CANCELED;

  return settled && Number(receiptStatus) >= RECEIPT_STATUS.READY;
}

/**
 * Whether the ride is still ahead, so an ETA and a tracking link mean something.
 *
 * Everything up to and including the ride itself. Once a trip is completed,
 * cancelled or needing attention there is no car coming: the ETAs are stale and
 * Uber's tracking link no longer resolves, so neither is worth showing.
 */
export function hasLiveTracking(status) {
  const group = statusDescriptor(status).group;
  return group === STATUS_GROUP.SCHEDULED || group === STATUS_GROUP.ONGOING;
}

/**
 * Whether a trip is in a state where re-booking makes sense.
 */
export function isRebookable(status) {
  return CRITICAL_STATUSES.includes(String(status || '').toLowerCase());
}
